/**
 * The customer's bills: invoices, advance receipts and refunds.
 *
 *   <BillsTab />                 — every bill, for the profile's Bills tab.
 *   <ServiceRequestBills id />   — one request's bills, compact; renders
 *                                  nothing until there is at least one.
 *
 * Cancelled bills stay listed with a muted "Cancelled" badge.
 */
import * as React from 'react';
import { Download, Eye, Loader2, Receipt } from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  ErrorMessage,
  Skeleton,
  cn,
} from '@finmin/ui';
import { BILL_KIND_LABELS, useMyBills, type PartyDocument } from '@/api/billing';
import { downloadBillPdf, viewBillPdf } from './open-bill-pdf';

// ── Formatting ────────────────────────────────────────────────────────────────

const inr = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function formatBillAmount(amount: string): string {
  const n = Number(amount);
  return Number.isFinite(n) ? inr.format(n) : amount;
}

/** "2026-10-13" → "13 Oct 2026", without a timezone shift. */
function formatBillDate(isoDate: string | null): string {
  if (!isoDate) return '—';
  const [y, m, d] = isoDate.split('-').map(Number);
  if (!y || !m || !d) return isoDate;
  return new Date(y, m - 1, d).toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function sortNewestFirst(bills: PartyDocument[]): PartyDocument[] {
  return [...bills].sort((a, b) => (b.issue_date ?? '').localeCompare(a.issue_date ?? ''));
}

// ── Row ───────────────────────────────────────────────────────────────────────

type PendingAction = { id: string; action: 'view' | 'download' } | null;

function BillRow({
  bill,
  pending,
  onView,
  onDownload,
  compact = false,
}: {
  bill: PartyDocument;
  pending: PendingAction;
  onView: (bill: PartyDocument) => void;
  onDownload: (bill: PartyDocument) => void;
  compact?: boolean;
}) {
  const kind = BILL_KIND_LABELS[bill.document_kind] ?? bill.document_kind;
  const label = `${kind} ${bill.number ?? ''}`.trim();
  const cancelled = bill.status === 'cancelled';
  const viewing = pending?.id === bill.id && pending.action === 'view';
  const downloading = pending?.id === bill.id && pending.action === 'download';

  return (
    <li className="py-4 first:pt-0 last:pb-0">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 space-y-1.5">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="tint">{kind}</Badge>
            {cancelled && (
              <Badge variant="outline" className="border-border text-muted-foreground">
                Cancelled
              </Badge>
            )}
          </div>
          <p
            className={cn(
              'break-all text-sm font-medium tabular-nums text-foreground',
              cancelled && 'text-muted-foreground line-through',
            )}
          >
            {bill.number ?? 'Unnumbered'}
          </p>
          <p className="text-xs text-muted-foreground">{formatBillDate(bill.issue_date)}</p>
        </div>
        <p
          className={cn(
            'shrink-0 text-right text-base font-semibold tabular-nums text-foreground',
            cancelled && 'text-muted-foreground',
          )}
        >
          {bill.total_amount === null ? '—' : formatBillAmount(bill.total_amount)}
        </p>
      </div>

      <div className={cn('mt-3 grid grid-cols-2 gap-2', !compact && 'sm:flex sm:justify-end')}>
        <Button
          type="button"
          variant="outline"
          className="h-11 gap-1.5 px-4"
          onClick={() => onView(bill)}
          disabled={viewing}
          aria-label={`View PDF of ${label}`}
        >
          {viewing ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          ) : (
            <Eye className="h-4 w-4" aria-hidden />
          )}
          View PDF
        </Button>
        <Button
          type="button"
          variant="outline"
          className="h-11 gap-1.5 px-4"
          onClick={() => onDownload(bill)}
          disabled={downloading}
          aria-label={`Download PDF of ${label}`}
        >
          {downloading ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          ) : (
            <Download className="h-4 w-4" aria-hidden />
          )}
          Download
        </Button>
      </div>
    </li>
  );
}

function useBillActions() {
  const [pending, setPending] = React.useState<PendingAction>(null);

  // Called straight from the click: viewBillPdf opens its tab before awaiting.
  function view(bill: PartyDocument) {
    setPending({ id: bill.id, action: 'view' });
    void viewBillPdf(bill).finally(() => setPending(null));
  }

  function download(bill: PartyDocument) {
    setPending({ id: bill.id, action: 'download' });
    void downloadBillPdf(bill).finally(() => setPending(null));
  }

  return { pending, view, download };
}

// ── Profile tab ───────────────────────────────────────────────────────────────

export function BillsTab() {
  const { data, isLoading, error } = useMyBills();
  const { pending, view, download } = useBillActions();
  const bills = React.useMemo(() => sortNewestFirst(data ?? []), [data]);

  if (isLoading) {
    return <Skeleton className="h-48 w-full rounded-xl" />;
  }

  return (
    <div className="space-y-5">
      <ErrorMessage message={error?.message ?? null} />
      {bills.length === 0 && !error ? (
        <Card>
          <CardContent className="py-10 text-center">
            <Receipt className="mx-auto h-10 w-10 text-muted-foreground/60" aria-hidden />
            <p className="mt-3 text-sm font-medium text-foreground">No bills yet</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Your bills will appear here after you pay for a service.
            </p>
          </CardContent>
        </Card>
      ) : bills.length > 0 ? (
        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 font-display text-lg">
              <Receipt className="h-4 w-4 text-muted-foreground" aria-hidden />
              Your bills
            </CardTitle>
            <CardDescription>Invoices, advance receipts and refunds for your services.</CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="divide-y divide-border" aria-label="Your bills">
              {bills.map((bill) => (
                <BillRow
                  key={bill.id}
                  bill={bill}
                  pending={pending}
                  onView={view}
                  onDownload={download}
                />
              ))}
            </ul>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}

// ── Per service request ───────────────────────────────────────────────────────

export function ServiceRequestBills({
  serviceRequestId,
  className,
  containerClassName,
}: {
  serviceRequestId: string;
  className?: string;
  /** Optional outer wrapper (page gutters); only rendered when there are bills. */
  containerClassName?: string;
}) {
  const { data } = useMyBills(serviceRequestId, { enabled: Boolean(serviceRequestId) });
  const { pending, view, download } = useBillActions();
  const bills = React.useMemo(() => sortNewestFirst(data ?? []), [data]);
  // Unique per instance: the workspace mounts this twice (mobile + sidebar).
  const headingId = React.useId();

  if (bills.length === 0) return null;

  const block = (
    <section
      aria-labelledby={headingId}
      className={cn('rounded-2xl border border-border bg-background p-4 shadow-sm', className)}
    >
      <h2
        id={headingId}
        className="mb-3 flex items-center gap-2 text-sm font-semibold text-foreground"
      >
        <Receipt className="h-4 w-4 text-muted-foreground" aria-hidden />
        Bills
      </h2>
      <ul className="divide-y divide-border">
        {bills.map((bill) => (
          <BillRow
            key={bill.id}
            bill={bill}
            pending={pending}
            onView={view}
            onDownload={download}
            compact
          />
        ))}
      </ul>
    </section>
  );

  return containerClassName ? <div className={containerClassName}>{block}</div> : block;
}
